import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';
import { CustomFieldTypeName, type CustomFieldValue } from '@air/api/types';
import {
  contentCollectionClipsInvalidFieldsSelector,
  contentCollectionRequiredFieldsSelector,
} from '@air/feature-content-collection/redux';
import { Label } from '@air/primitive-label';
import { cn } from '@air/utils-tailwind';

import { useContentCollectionCustomFieldOptions } from '~/components/ContentCollection/hooks/useContentCollectionCustomFieldOptions';
import { chipToCustomField } from '~/components/CustomFields/CustomFieldInput/utils';
import { Select, type SelectProps } from '~/components/Select/Select';
import { type DefaultChipType } from '~/components/Select/shared/types';
import { type AirState } from '~/store/types';
import { type AssignedCustomFieldValue } from '~/swr-hooks/customFields/types';

export type ContentCollectionBaseCustomFieldsProps = {
  field: AssignedCustomFieldValue;
  clips: PublicClip[];
  disabled?: boolean;
  showDescription?: boolean;
};

export type ContentCollectionCustomFieldsSelectProps = {
  onSelectionChange: (params: { values: CustomFieldValue[] | null; allValueIds?: string[] }) => void;
} & ContentCollectionBaseCustomFieldsProps &
  Pick<SelectProps, 'size'>;

export const ContentCollectionCustomFieldsSelect = memo(
  ({
    disabled = false,
    field,
    onSelectionChange,
    clips,
    size = 'large',
    showDescription = false,
  }: ContentCollectionCustomFieldsSelectProps) => {
    const { data: customFieldValues } = useContentCollectionCustomFieldOptions({
      customFieldId: field.id,
      customFieldTypeName: field.type.title,
    });

    const requiredFields = useSelector(contentCollectionRequiredFieldsSelector);
    const invalidFields = useSelector((st: AirState) =>
      contentCollectionClipsInvalidFieldsSelector(
        st,
        clips.map(({ id }) => id),
      ),
    );

    const showInvalidState = !disabled && invalidFields.some((f) => f.customFieldId === field.id);
    const required = !disabled && requiredFields.some((f) => f.customFieldId === field.id);

    const selectedOptions = useMemo<DefaultChipType[]>(() => {
      if (field.value) {
        return [
          {
            value: field.value.id,
            label: field.value.value,
            borderRadius: 4,
            backgroundHexColor: field.value.color.backgroundHex,
            fontHexColor: field.value.color.fontHex,
          },
        ];
      }
      if (field.values) {
        return field.values.map((fieldValue) => ({
          value: fieldValue.id,
          label: fieldValue.value,
          borderRadius: 4,
          backgroundHexColor: fieldValue.color.backgroundHex,
          fontHexColor: fieldValue.color.fontHex,
        }));
      }
      return [];
    }, [field]);

    const options = useMemo<DefaultChipType[]>(() => {
      if (customFieldValues) {
        return customFieldValues.data.map((fieldValue) => ({
          value: fieldValue.id,
          label: fieldValue.value,
          borderRadius: 4,
          backgroundHexColor: fieldValue.color.backgroundHex,
          fontHexColor: fieldValue.color.fontHex,
        }));
      } else {
        return [];
      }
    }, [customFieldValues]);

    const _onSelectionChange: SelectProps<DefaultChipType>['onSelectionChange'] = useCallback(
      async ({ chips }) => {
        if (customFieldValues?.data) {
          if (field.type.title === CustomFieldTypeName.singleSelect) {
            const value = chips[0] ? chipToCustomField(chips[0], customFieldValues.data) : null;

            onSelectionChange({
              values: value ? [value] : null,
            });
          } else {
            onSelectionChange({
              values: chips.length
                ? chips.reduce((curr, chip) => {
                    const cfValue = customFieldValues.data.find(({ id }) => chip.value === id);

                    if (cfValue) {
                      curr.push(cfValue);
                    }

                    return curr;
                  }, [] as CustomFieldValue[])
                : null,
              allValueIds: chips.map(({ value }) => value),
            });
          }
        }
      },
      [customFieldValues?.data, field.type.title, onSelectionChange],
    );

    return (
      <div className="flex w-full flex-col gap-1">
        <Label className="flex items-center justify-between font-semibold" htmlFor={field.id}>
          {field.name}
          {required && <span className={cn('text-10 uppercase', showInvalidState && 'text-red-9')}>Required</span>}
        </Label>
        <Select
          data-testid={'CONTENT_COLLECTION_CUSTOM_FIELD_SELECT'}
          data-field-name={field.name}
          data-is-invalid={showInvalidState}
          closeMenuOnSelect={field.type.title === CustomFieldTypeName.singleSelect}
          options={options}
          placeholder="None"
          selectedOptions={selectedOptions}
          isSearchable
          className={cn('[&>button]:!rounded-8 w-full', disabled && '[&>button]:!bg-gray-3 [&>button]:!text-gray-12')}
          inputContainerClassName={cn('!rounded-8', showInvalidState && '!ring-1 !ring-red-9')}
          maxDropdownHeight={300}
          onSelectionChange={_onSelectionChange}
          isSingleSelect={field.type.title === CustomFieldTypeName.singleSelect}
          isClearable
          size={size}
          renderAsInput={true}
          hasError={showInvalidState}
          disabled={disabled}
          isSticky
          usePortal
        />
        {showDescription && field.description && <p className="text-12 text-gray-10">{field.description}</p>}
      </div>
    );
  },
);

ContentCollectionCustomFieldsSelect.displayName = 'ContentCollectionCustomFieldsSelect';
